import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import CertificateLink from './CertificateLink';
import { overlayDismiss } from '../utils/modalDismiss';
import { download, openPdf, formatDate, emailSelected, AWARD_NAMES, confirmIssue, certCounts } from '../utils/certificates';

const LEVELS = [['attendance', 'Attendance'], ['achievement', 'Completion']];

const SettingsModal = ({ trainingId, settings, onClose, onSaved }) => {
  const [form, setForm] = useState({
    template_id: settings.template_id || '',
    attendance_template_id: settings.attendance_template_id || '',
    company: settings.company || '',
    quiz_ids: settings.quiz_ids || [],
    min_score: settings.min_score ?? 80,
    full_attendance: settings.full_attendance !== false
  });
  const [templates, setTemplates] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/certificates/templates').then(r => setTemplates(r.data)).catch(() => {});
    api.get('/certificates/quizzes').then(r => setQuizzes(r.data)).catch(() => {});
  }, []);

  const toggleQuiz = (id) => setForm(f => ({
    ...f, quiz_ids: f.quiz_ids.includes(id) ? f.quiz_ids.filter(x => x !== id) : [...f.quiz_ids, id]
  }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/certificates/trainings/${trainingId}`, { ...form, min_score: Number(form.min_score) });
      onSaved();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const needle = q.trim().toLowerCase();
  // Chosen assessments first, then the rest.
  const list = [...quizzes]
    .filter(z => form.quiz_ids.includes(z.id) || !needle || z.title.toLowerCase().includes(needle) || (z.category_name || '').toLowerCase().includes(needle))
    .sort((a, b) => form.quiz_ids.includes(b.id) - form.quiz_ids.includes(a.id));

  return (
    <div className="modal-overlay" {...overlayDismiss(onClose)}>
      <div className="modal" style={{ maxWidth: 720, maxHeight: '92vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h3>Certificate settings</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body">
            <div className="grid grid-2">
              <div className="form-group">
                <label>Attendance certificate</label>
                <select value={form.attendance_template_id} onChange={e => setForm({ ...form, attendance_template_id: e.target.value })}>
                  <option value="">None</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <small className="text-muted">For everyone who attended as required</small>
              </div>
              <div className="form-group">
                <label>Completion certificate</label>
                <select value={form.template_id} onChange={e => setForm({ ...form, template_id: e.target.value })}>
                  <option value="">None</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <small className="text-muted">Also for those who reach the score below</small>
              </div>
            </div>
            {templates.length === 0 && (
              <p className="text-sm text-muted">No templates yet — an admin creates them under <Link to="/certificates">Certificates</Link>.</p>
            )}
            <div className="form-group">
              <label>Company (customer)</label>
              <input type="text" maxLength={255} placeholder="e.g. Dr. Reddy's Laboratories Ltd" value={form.company}
                onChange={e => setForm({ ...form, company: e.target.value })} />
            </div>
            <label className="flex flex-center gap-2 mb-3">
              <input type="checkbox" checked={form.full_attendance} onChange={e => setForm({ ...form, full_attendance: e.target.checked })} />
              Require 100% attendance (present every day)
            </label>

            <h4 className="mb-2">Assessments for the completion certificate</h4>
            <p className="text-sm text-muted mb-2">
              The combined score is total marks over total possible across these, using each trainee's best graded
              attempt. Trainees are matched to their attempts by email.
            </p>
            <div className="form-group" style={{ maxWidth: 220 }}>
              <label>Minimum combined score (%)</label>
              <input type="number" min={0} max={100} required value={form.min_score} onChange={e => setForm({ ...form, min_score: e.target.value })} />
            </div>
            <input type="search" placeholder="Search assessments" value={q} onChange={e => setQ(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, marginBottom: 8 }} />
            <div style={{ maxHeight: 220, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px' }}>
              {list.map(z => (
                <label key={z.id} className="flex flex-center gap-2 text-sm" style={{ padding: '5px 0' }}>
                  <input type="checkbox" checked={form.quiz_ids.includes(z.id)} onChange={() => toggleQuiz(z.id)} />
                  <span>{z.title}{z.category_name && <span className="text-muted"> · {z.category_name}</span>}</span>
                </label>
              ))}
              {list.length === 0 && <p className="text-sm text-muted">No assessments found</p>}
            </div>
            <p className="text-sm text-muted mt-2">{form.quiz_ids.length} selected. With none selected, the completion certificate needs attendance only.</p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TrainingCertificates = ({ training }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/certificates/trainings/${training.id}`)
      .then(r => { setData(r.data); setError(''); })
      .catch(err => setError(err.response?.data?.error || 'Failed to load certificates'));
  }, [training.id]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="alert alert-error mt-4">{error}</div>;
  if (!data) return null;

  const { settings, trainees, quizzes } = data;
  const configured = settings.template_id || settings.attendance_template_id;
  const ready = trainees.flatMap(t => LEVELS.filter(([l]) => t[l].eligible && !t[l].certificate).map(([l]) => ({ t, l })));
  const certs = trainees.flatMap(t => LEVELS.filter(([l]) => t[l].certificate).map(([l]) => ({
    ...t[l].certificate, level: l, recipient_name: t.name, recipient_email: t.email
  })));
  const chosenPeople = trainees.filter(t => selected.has(t.employee_id));
  const chosen = certs.filter(c => chosenPeople.some(t => t[c.level].certificate?.id === c.id));
  const toggle = (id) => setSelected(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // The trainer's choice for trainees: 'auto' (follow the rule) or a fixed award.
  const decide = async (ids, award) => {
    try {
      await api.put(`/certificates/trainings/${training.id}/overrides`, { employee_ids: ids, award });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    }
  };

  const issue = async () => {
    const att = ready.filter(r => r.l === 'attendance').length;
    const ach = ready.filter(r => r.l === 'achievement').length;
    if (!confirmIssue(new Set(ready.map(r => r.t.employee_id)).size, ach, att)) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/certificates/trainings/${training.id}/issue`, {});
      alert(`Issued: ${certCounts(r.achievement, r.attendance)}. Nothing has been emailed.`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to issue');
    } finally {
      setBusy(false);
    }
  };

  const cell = (t, level) => {
    const s = t[level];
    if (!s.enabled) return <span className="text-muted">—</span>;
    if (s.certificate) {
      const c = s.certificate;
      return (
        <span className="text-sm" style={{ whiteSpace: 'nowrap' }}>
          <CertificateLink cert={c} name={t.name} />
          {c.emailed_at && <span className="text-muted" title={`Emailed ${formatDate(c.emailed_at)}`}> ✉</span>}
          {s.reasons.length > 0 && (
            <div style={{ color: '#b45309', whiteSpace: 'normal', maxWidth: 220 }}>
              Issued earlier but no longer qualifies — withdraw it under Certificates › Issued if not wanted
            </div>
          )}
        </span>
      );
    }
    if (s.eligible) return <span className="badge badge-info">Ready to issue</span>;
    return <span className="text-sm text-muted">{s.reasons.join('; ')}</span>;
  };

  return (
    <div className="card mt-4" style={{ padding: 0 }}>
      <div className="flex flex-between flex-center" style={{ padding: '16px 20px', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h3 style={{ margin: 0 }}>Certificates</h3>
          {configured && (
            <p className="text-sm text-muted" style={{ margin: '4px 0 0' }}>
              {settings.full_attendance ? '100% attendance' : 'Attendance'}
              {settings.template_id && (quizzes.length
                ? ` · completion needs ${settings.min_score}% combined across ${quizzes.length} assessment${quizzes.length === 1 ? '' : 's'}`
                : ' · completion needs attendance only')}
              {settings.company && ` · ${settings.company}`}
            </p>
          )}
        </div>
        <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>Settings</button>
          {configured && <>
            <button className="btn btn-primary btn-sm" onClick={issue} disabled={busy || !ready.length}>Issue certificates ({ready.length})</button>
            <button className="btn btn-secondary btn-sm" disabled={!certs.length}
              onClick={() => download(`/certificates/trainings/${training.id}/zip`, `Certificates_${training.title.replace(/[^a-zA-Z0-9]+/g, '_')}.zip`)}>
              Download all (ZIP)
            </button>
          </>}
        </div>
      </div>
      {configured && trainees.length > 0 && (
        <div className="flex flex-center gap-2" style={{ padding: '0 20px 12px', flexWrap: 'wrap' }}>
          <span className="text-sm text-muted">{chosenPeople.length} selected:</span>
          {Object.entries(AWARD_NAMES).map(([k, v]) => (
            <button key={k} className="btn btn-secondary btn-sm" disabled={!chosenPeople.length}
              onClick={() => decide(chosenPeople.map(t => t.employee_id), k)}>Set to {v}</button>
          ))}
          <button className="btn btn-secondary btn-sm" disabled={!chosenPeople.some(t => t.override)}
            onClick={() => decide(chosenPeople.map(t => t.employee_id), 'auto')}>Back to automatic</button>
          <button className="btn btn-secondary btn-sm" disabled={!chosen.length}
            onClick={() => emailSelected(`/certificates/trainings/${training.id}/email`, chosen, () => { setSelected(new Set()); load(); })}>
            Email selected ({chosen.length} certificate{chosen.length === 1 ? '' : 's'})
          </button>
        </div>
      )}
      {!configured ? (
        <p className="text-muted text-center" style={{ padding: '0 20px 20px' }}>
          Choose the certificates for this training in Settings.
        </p>
      ) : trainees.length === 0 ? (
        <p className="text-muted text-center" style={{ padding: '0 20px 20px' }}>No trainees have marked attendance yet.</p>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input type="checkbox" checked={chosenPeople.length === trainees.length} title="Select all"
                    onChange={e => setSelected(e.target.checked ? new Set(trainees.map(t => t.employee_id)) : new Set())} />
                </th>
                <th>Trainee</th><th>Days</th>
                {quizzes.map(z => <th key={z.id} className="text-sm" style={{ maxWidth: 140 }}>{z.title}</th>)}
                {quizzes.length > 1 && <th>Combined</th>}
                <th>Certificates</th>
                <th>Attendance</th>
                <th>Completion</th>
              </tr>
            </thead>
            <tbody>
              {trainees.map(t => (
                <tr key={t.employee_id}>
                  <td><input type="checkbox" checked={selected.has(t.employee_id)} onChange={() => toggle(t.employee_id)} /></td>
                  <td><strong>{t.name}</strong><div className="text-sm text-muted">{t.employee_id} · {t.email || 'no email'}</div></td>
                  <td>{t.days_present}/{t.days_total}</td>
                  {t.scores.map(s => <td key={s.quiz_id} className="text-sm">{s.percentage === null ? '—' : `${s.percentage.toFixed(0)}%`}</td>)}
                  {quizzes.length > 1 && <td className="text-sm"><strong>{t.combined === null ? '—' : `${t.combined}%`}</strong></td>}
                  <td>
                    <select value={t.override ? t.override.award : 'auto'} onChange={e => decide([t.employee_id], e.target.value)}
                      style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, background: t.override ? '#fff7ed' : '#fff' }}>
                      <option value="auto">Automatic: {AWARD_NAMES[t.rule_award]}</option>
                      {Object.entries(AWARD_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    {t.override && (
                      <div className="text-sm text-muted" style={{ marginTop: 2 }}>
                        Set by {t.override.set_by_name || 'trainer'}, {formatDate(t.override.set_at)}
                      </div>
                    )}
                  </td>
                  <td>{cell(t, 'attendance')}</td>
                  <td>{cell(t, 'achievement')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {configured && trainees.length > 0 && (
        <p className="text-sm text-muted" style={{ padding: '0 20px 16px', margin: 0 }}>
          "Automatic" follows the rule above; choose another option to decide yourself (e.g. a paper assessment).
          Issuing never emails anyone. Tick trainees, then "Email selected" to send their certificates.
        </p>
      )}
      {editing && (
        <SettingsModal trainingId={training.id} settings={settings}
          onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />
      )}
    </div>
  );
};

export default TrainingCertificates;
